import Phaser from 'phaser';
import { CLASSIC_BOARD_GEOMETRY } from '../logic/classicBoardAccess';
import type { Key9Puzzle, Key9Result } from '../types/puzzle';
import { classifyResult } from '../logic/key9Validator';
import { applyKey9Attempt, getKey9SolvedDigits, resolveKey9TileTap } from '../logic/key9Interaction';
import {
  createKey9ProgressSnapshot,
  restoreKey9ProgressState
} from '../logic/key9Progress';
import {
  applyFullHint,
  canUseFullHint,
  clearFullNotesForCell,
  createFullBoard,
  createFullNotes,
  eraseFullCellEntry,
  getFullSudokuConflicts,
  isFullSudokuComplete,
  resolveFullNumberTap,
  toggleFullNote
} from '../logic/fullSudoku';
import { KEY9_MARKER_CLASS_NAMES, getKey9MarkerGeometry } from '../logic/key9Marker';
import { getTileBasePosition, resetTileVisualSnapshot, type TileBasePosition } from '../logic/tileVisualState';
import { getBoardFeedbackCleanup, getCellBaseRect, resetBoardVisualSnapshot } from '../logic/boardVisualState';
import {
  beginKey9PlacementAttempt,
  createKey9InputGateState,
  enterKey9TerminalState,
  finishKey9PlacementFeedback,
  resetKey9InputGate,
  type Key9InputGateState
} from '../logic/key9InputGate';
import type { DayProgress } from '../storage/localSave';
import { FULL_SUDOKU_INPUT_LAYOUT, canContinueSudokuFromSceneState } from '../logic/gameControls';
import {
  detectNewlyCompletedUnits,
  getBoardHighlightTargets,
  getKey9UnlockCelebration,
  type BoardHighlightTargets,
  type CompletedUnit
} from '../logic/sudokuComfort';

const BOARD_COLORS = {
  background: 0xf5f7fb,
  emptyCell: 0xf8fafc,
  givenCell: 0xffffff,
  givenText: '#1f2937',
  thinLine: 0xcbd5e1,
  boxLine: 0x334155,
  outerLine: 0x0f172a,
  keyFill: 0xdbeafe,
  keyBorder: 0x60a5fa,
  keyBorderActive: 0x1d4ed8,
  keyText: '#1d4ed8',
  keyMarker: 0x3b82f6,
  keyMarkerMuted: 0x64748b,
  keyMarkerLocked: 0x16a34a,
  successFill: 0xdcfce7,
  successFlash: 0xbbf7d0,
  successBorder: 0x16a34a,
  successText: '#15803d',
  lockedKeyFill: 0xf8fff9,
  lockedKeyBorder: 0xbbf7d0,
  lockedKeyText: '#166534',
  wrongFill: 0xfee2e2,
  wrongBorder: 0xdc2626,
  conflictFill: 0xfef2f2,
  conflictBorder: 0xef4444,
  conflictText: '#dc2626',
  peerHighlightFill: 0xe8eef7,
  sameDigitFill: 0xdbeafe,
  selectedFill: 0xbfdbfe,
  completionFill: 0xbfdbfe,
  completionBorder: 0x60a5fa,
  unlockFill: 0xdcfce7,
  userText: '#1d4ed8',
  subtleFill: 0xe2e8f0
};

const TILE_COLORS = {
  fill: 0xffffff,
  border: 0x94a3b8,
  text: '#111827',
  selectedFill: 0xfef3c7,
  selectedBorder: 0xd97706,
  selectedText: '#78350f',
  usedFill: 0xe2e8f0,
  usedBorder: 0xcbd5e1,
  usedText: '#64748b',
  shadow: 0x0f172a
};

export class Key9Scene extends Phaser.Scene {
  private puzzle!: Key9Puzzle;
  private mode: 'key9' | 'full' = 'key9';
  private practice = false;
  private officialKey9Missed = false;
  private savedFullBoard?: string[];
  private savedFullNotes?: string[];
  private savedFullHints = 0;
  private savedKey9Seconds = 0;
  private boardOriginX = CLASSIC_BOARD_GEOMETRY.x;
  private boardOriginY = CLASSIC_BOARD_GEOMETRY.y;
  private cellSize = CLASSIC_BOARD_GEOMETRY.cellSize;
  private selectedTileIndex: number | null = null;
  private selectedCellIndex: number | null = null;
  private selectedNumber: number | null = null;
  private notesMode = false;
  private misses = 0;
  private hints = 0;
  private fullHints = 0;
  private completed = false;
  private startMs = 0;
  private keySet = new Set<number>();
  private lockedCells = new Map<number, number>();
  private usedTiles = new Set<number>();
  private undoStack: { cell: number; tileIndex: number; digit: number }[] = [];
  private fullBoard: string[] = [];
  private fullNotes: string[] = createFullNotes();
  private fullLockedCells = new Set<number>();
  private conflictingFullCells = new Set<number>();
  private fullUndoStack: { cell: number; previous: string; next: string; previousNotes?: string }[] = [];
  private cellRects = new Map<number, Phaser.GameObjects.Rectangle>();
  private cellTexts = new Map<number, Phaser.GameObjects.Text>();
  private cellNoteTexts = new Map<number, Phaser.GameObjects.Text[]>();
  private cellMarkers = new Map<number, Phaser.GameObjects.Arc>();
  private cellFeedbackOverlays = new Set<Phaser.GameObjects.GameObject>();
  private tileContainers: Phaser.GameObjects.Container[] = [];
  private tileBgs: Phaser.GameObjects.Graphics[] = [];
  private tileTexts: Phaser.GameObjects.Text[] = [];
  private tileZones: Phaser.GameObjects.Zone[] = [];
  private tileBasePositions = new Map<number, TileBasePosition>();
  private key9InputGate: Key9InputGateState = createKey9InputGateState();
  private key9FeedbackEvent?: Phaser.Time.TimerEvent;
  private key9FeedbackToken = 0;
  private instructionText?: Phaser.GameObjects.Text;
  private trayLabel?: Phaser.GameObjects.Text;
  private boardGrid?: Phaser.GameObjects.Graphics;

  constructor() {
    super('Key9Scene');
  }

  private classic = false;

  init(data: { puzzle: Key9Puzzle; mode?: 'key9' | 'full'; progress?: DayProgress; practice?: boolean; classic?: boolean }) {
    this.resetSceneRuntimeState();
    this.puzzle = data.puzzle;
    this.classic = data.classic ?? false;
    this.keySet = new Set(this.classic ? [] : this.puzzle.keyCells);
    this.mode = data.mode ?? 'key9';
    this.practice = data.practice ?? false;
    this.officialKey9Missed = data.progress?.key9Result?.status === 'missed';
    this.savedFullBoard = data.progress?.fullBoard;
    this.savedFullNotes = data.progress?.fullNotes;
    this.savedFullHints = data.progress?.fullHints ?? 0;
    this.completed = data.progress?.phase === 'full_done';
    this.key9InputGate = createKey9InputGateState();
    this.key9FeedbackToken += 1;
    this.key9FeedbackEvent = undefined;
    this.cellFeedbackOverlays.clear();

    if (data.progress?.key9Result && !this.practice) {
      this.misses = data.progress.key9Result.misses;
      this.hints = data.progress.key9Result.hints;
    }

    if (this.mode === 'key9' && !this.practice && data.progress?.phase === 'key9_in_progress') {
      const restored = restoreKey9ProgressState(this.puzzle, data.progress.key9Progress);
      this.misses = restored.misses;
      this.hints = restored.hints;
      this.savedKey9Seconds = restored.seconds;
      this.lockedCells = restored.lockedCells;
      this.usedTiles = restored.usedTiles;
      this.undoStack = restored.undoStack;
    }

    if (this.mode === 'full') {
      this.prepareFullMode(this.savedFullBoard, this.savedFullNotes, this.savedFullHints);
    }
  }

  private resetSceneRuntimeState() {
    this.mode = 'key9';
    this.practice = false;
    this.officialKey9Missed = false;
    this.savedFullBoard = undefined;
    this.savedFullNotes = undefined;
    this.savedFullHints = 0;
    this.savedKey9Seconds = 0;
    this.selectedTileIndex = null;
    this.selectedCellIndex = null;
    this.selectedNumber = null;
    this.notesMode = false;
    this.misses = 0;
    this.hints = 0;
    this.fullHints = 0;
    this.completed = false;
    this.keySet = new Set();
    this.lockedCells = new Map();
    this.usedTiles = new Set();
    this.undoStack = [];
    this.fullBoard = [];
    this.fullNotes = createFullNotes();
    this.fullLockedCells = new Set();
    this.conflictingFullCells = new Set();
    this.fullUndoStack = [];
    this.cellRects = new Map();
    this.cellTexts = new Map();
    this.cellNoteTexts = new Map();
    this.cellMarkers = new Map();
    this.cellFeedbackOverlays = new Set();
    this.tileContainers = [];
    this.tileBgs = [];
    this.tileTexts = [];
    this.tileZones = [];
    this.tileBasePositions = new Map();
  }

  create() {
    this.cameras.main.setBackgroundColor('#f5f7fb');
    this.startMs = Date.now() - this.savedKey9Seconds * 1000;
    this.drawBoard();
    if (this.mode === 'full') this.fullNotes.forEach((_, cell) => this.updateCellNotes(cell));
    this.drawTiles();
    this.emitStatus();
  }

  public undo() {
    if (this.mode === 'full') {
      this.undoFullMove();
      return;
    }

    if (this.completed || this.isKey9InputLocked()) return;
    const last = this.undoStack.pop();
    if (!last) return;
    this.lockedCells.delete(last.cell);
    this.usedTiles.delete(last.tileIndex);
    this.redrawKeyCell(last.cell);
    this.redrawTile(last.tileIndex);
    this.setInstruction('Tap a marked cell, then place 1–9.');
    this.emitStatus();
  }

  public erase() {
    if (this.mode === 'full') {
      this.eraseFullCell();
      return;
    }

    if (this.completed || this.isKey9InputLocked() || this.selectedCellIndex == null) return;
    const cell = this.selectedCellIndex;
    if (!this.lockedCells.has(cell)) return;
    const entry = this.undoStack.slice().reverse().find((item) => item.cell === cell);
    if (!entry) return;
    this.lockedCells.delete(entry.cell);
    this.usedTiles.delete(entry.tileIndex);
    this.undoStack = this.undoStack.filter((item) => item.cell !== cell);
    this.redrawKeyCell(cell);
    this.redrawTile(entry.tileIndex);
    this.setInstruction('Tap a marked cell, then place 1–9.');
    this.emitStatus();
  }

  public hint() {
    if (this.mode === 'full') {
      this.hintFullCell();
      return;
    }

    if (this.completed || this.isKey9InputLocked()) return;
    this.hints += 1;
    const unresolved = this.puzzle.keyCells.find((cell) => !this.lockedCells.has(cell));
    if (unresolved == null) return;
    this.flashCell(unresolved, 0x2563eb);
    this.emitStatus();
  }

  public continueSudoku() {
    if (!canContinueSudokuFromSceneState(this.mode, this.completed)) return;
    this.cancelKey9FeedbackUnlock();
    this.key9InputGate = resetKey9InputGate();
    this.cleanupBoardFeedback({ terminal: false });
    this.resetTileTrayVisualState({ repaint: false });
    this.mode = 'full';
    this.completed = false;
    this.startMs = Date.now();
    this.selectedTileIndex = null;
    this.selectedCellIndex = null;
    this.selectedNumber = null;
    this.notesMode = false;
    this.prepareFullMode(this.savedFullBoard, this.savedFullNotes, this.savedFullHints);
    this.repaintBoard();
    this.drawTiles();
    this.setInstruction('Tap an empty cell, then enter a number.');
    this.emitFullProgress();
    this.emitStatus();
  }

  public retryKey9Practice() {
    this.cancelKey9FeedbackUnlock();
    this.key9InputGate = resetKey9InputGate();
    this.selectedTileIndex = null;
    this.selectedCellIndex = null;
    this.selectedNumber = null;
    this.notesMode = false;
    this.cleanupBoardFeedback({ terminal: false });
    this.resetTileTrayVisualState({ repaint: false });
    this.mode = 'key9';
    this.practice = true;
    this.officialKey9Missed = true;
    this.completed = false;
    this.startMs = Date.now();
    this.misses = 0;
    this.hints = 0;
    this.lockedCells = new Map();
    this.usedTiles = new Set();
    this.undoStack = [];
    this.fullLockedCells = new Set();
    this.repaintBoard();
    this.drawTiles();
    this.setInstruction('Practice: tap a marked cell, then place 1–9.');
    this.emitStatus();
  }

  public toggleNotes(): boolean {
    if (this.mode !== 'full' || this.completed) return this.notesMode;
    this.notesMode = !this.notesMode;
    this.selectedNumber = null;
    this.highlightSelectedTile();
    this.setInstruction(this.notesMode ? 'Notes on: tap an empty cell, then candidate numbers.' : 'Notes off: tap an empty cell, then enter a number.');
    this.emitStatus();
    return this.notesMode;
  }

  private drawBoard() {
    for (let r = 0; r < 9; r += 1) {
      for (let c = 0; c < 9; c += 1) {
        const index = r * 9 + c;
        const base = this.getCellBaseRect(index);
        const value = this.getCellValue(index);
        const rect = this.add.rectangle(
          base.centerX,
          base.centerY,
          base.width,
          base.height,
          this.getCellFill(index)
        );
        rect.setDepth(1);
        if (this.keySet.has(index)) {
          rect.setName(KEY9_MARKER_CLASS_NAMES.cell);
        }
        if (!this.classic) {
          rect.setInteractive({ useHandCursor: this.mode === 'full' || this.isCellActionable(index) });
          rect.on('pointerdown', () => this.onCellClick(index));
        }
        this.cellRects.set(index, rect);
        this.paintCellStroke(index);
        this.drawKey9Marker(index);
        if (value !== '0') {
          const txt = this.add.text(base.centerX, base.centerY, value, {
            fontFamily: 'Arial, sans-serif',
            fontSize: '22px',
            fontStyle: this.classic ? '500' : this.fullLockedCells.has(index) ? '800' : '700',
            color: this.getCellTextColor(index)
          }).setOrigin(0.5);
          txt.setDepth(6);
          this.cellTexts.set(index, txt);
        }
      }
    }

    this.drawBoardGrid();
  }

  private drawBoardGrid() {
    this.boardGrid?.destroy();
    const g = this.add.graphics();
    g.setDepth(5);

    for (let i = 0; i <= 9; i += 1) {
      g.lineStyle(1, BOARD_COLORS.thinLine, 1);
      g.lineBetween(this.boardOriginX, this.boardOriginY + i * this.cellSize, this.boardOriginX + 9 * this.cellSize, this.boardOriginY + i * this.cellSize);
      g.lineBetween(this.boardOriginX + i * this.cellSize, this.boardOriginY, this.boardOriginX + i * this.cellSize, this.boardOriginY + 9 * this.cellSize);
    }

    for (let i = 0; i <= 9; i += 1) {
      if (i % 3 !== 0) continue;
      const isOuter = i === 0 || i === 9;
      g.lineStyle(this.classic ? (isOuter ? 2.5 : 2) : isOuter ? 4 : 3, isOuter ? BOARD_COLORS.outerLine : BOARD_COLORS.boxLine, 1);
      g.lineBetween(this.boardOriginX, this.boardOriginY + i * this.cellSize, this.boardOriginX + 9 * this.cellSize, this.boardOriginY + i * this.cellSize);
      g.lineBetween(this.boardOriginX + i * this.cellSize, this.boardOriginY, this.boardOriginX + i * this.cellSize, this.boardOriginY + 9 * this.cellSize);
    }

    this.boardGrid = g;
  }

  private drawKey9Marker(cellIndex: number) {
    const existing = this.cellMarkers.get(cellIndex);
    existing?.destroy();
    this.cellMarkers.delete(cellIndex);

    if (!this.keySet.has(cellIndex)) return;

    const base = this.getCellBaseRect(cellIndex);
    const marker = getKey9MarkerGeometry(this.cellSize);
    const dot = this.add.circle(
      base.centerX + marker.offsetX,
      base.centerY + marker.offsetY,
      marker.radius,
      this.getKey9MarkerColor(cellIndex),
      this.getKey9MarkerAlpha(cellIndex)
    );

    dot.setDepth(4.5);
    dot.setName(KEY9_MARKER_CLASS_NAMES.marker);
    dot.setData('classNames', this.getKey9MarkerClassNames(cellIndex));
    this.cellMarkers.set(cellIndex, dot);
  }

  private refreshKey9Marker(cellIndex: number) {
    if (!this.keySet.has(cellIndex)) return;
    this.drawKey9Marker(cellIndex);
  }

  private getKey9MarkerColor(cellIndex: number): number {
    if (this.mode === 'full') {
      return this.fullLockedCells.has(cellIndex) ? BOARD_COLORS.keyMarkerLocked : BOARD_COLORS.keyMarkerMuted;
    }
    return this.lockedCells.has(cellIndex) ? BOARD_COLORS.keyMarkerLocked : BOARD_COLORS.keyMarker;
  }

  private getKey9MarkerAlpha(cellIndex: number): number {
    if (this.mode === 'full') {
      return this.fullLockedCells.has(cellIndex) ? 0.58 : 0.36;
    }
    return this.lockedCells.has(cellIndex) ? 0.62 : 0.78;
  }

  private getKey9MarkerClassNames(cellIndex: number): string[] {
    const classNames: string[] = [KEY9_MARKER_CLASS_NAMES.cell, KEY9_MARKER_CLASS_NAMES.marker];
    if (this.lockedCells.has(cellIndex) || this.fullLockedCells.has(cellIndex)) {
      classNames.push(KEY9_MARKER_CLASS_NAMES.locked);
    }
    if (this.mode === 'full') {
      classNames.push(KEY9_MARKER_CLASS_NAMES.fullMode);
    }
    return classNames;
  }

  private drawTiles() {
    this.clearTray();
    if (this.classic) {
      this.drawFullDoneMessage();
      this.setInstruction(this.completed ? 'Solved for today.' : 'Select a cell, then enter a number.');
      return;
    }
    if (this.mode === 'full') {
      if (this.completed) {
        this.drawFullDoneMessage();
        return;
      }
      this.drawNumberPad();
      return;
    }
    this.drawKey9Tiles();
  }

  private drawKey9Tiles() {
    const startY = 456;
    const tileW = 38;
    const tileH = 42;
    const gap = 3;
    const totalW = this.puzzle.keyTiles.length * tileW + (this.puzzle.keyTiles.length - 1) * gap;
    const startX = this.boardOriginX + ((9 * this.cellSize) - totalW) / 2;

    this.instructionText = this.add.text(this.boardOriginX + (9 * this.cellSize) / 2, 414, this.practice ? 'Practice: tap a marked cell, then place 1–9.' : 'Tap a marked cell, then place 1–9.', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      fontStyle: '700',
      color: '#475569'
    }).setOrigin(0.5);

    this.trayLabel = this.add.text(this.boardOriginX, startY - 27, this.practice ? 'Key9 Practice 1–9' : 'Key9 tiles 1–9', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      fontStyle: '700',
      color: '#334155'
    });
    this.puzzle.keyTiles.forEach((digit, i) => {
      const base = getTileBasePosition({ index: i, startX, startY, tileW, tileH, gap });
      const bg = this.add.graphics();
      const text = this.add.text(0, 0, String(digit), {
        fontFamily: 'Arial, sans-serif',
        fontSize: '23px',
        fontStyle: '800',
        color: TILE_COLORS.text
      }).setOrigin(0.5);
      const container = this.add.container(base.x, base.y, [bg, text]).setDepth(8);
      const zone = this.add.zone(base.x, base.y, tileW, tileH)
        .setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => this.onTileClick(i));
      this.tileBasePositions.set(i, base);
      this.tileContainers[i] = container;
      this.tileBgs[i] = bg;
      this.tileTexts[i] = text;
      this.tileZones[i] = zone;
      this.paintTile(i);
    });
  }

  private drawNumberPad() {
    const startY = FULL_SUDOKU_INPUT_LAYOUT.numberPadY;
    const tileW = 38;
    const tileH = 42;
    const gap = 3;
    const totalW = 9 * tileW + 8 * gap;
    const startX = this.boardOriginX + ((9 * this.cellSize) - totalW) / 2;

    this.instructionText = this.add.text(this.boardOriginX + (9 * this.cellSize) / 2, FULL_SUDOKU_INPUT_LAYOUT.instructionY, 'Tap an empty cell, then enter a number.', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      fontStyle: '700',
      color: '#475569'
    }).setOrigin(0.5);

    for (let i = 0; i < 9; i += 1) {
      const digit = i + 1;
      const base = getTileBasePosition({ index: i, startX, startY, tileW, tileH, gap });
      const bg = this.add.graphics();
      const text = this.add.text(0, 0, String(digit), {
        fontFamily: 'Arial, sans-serif',
        fontSize: '23px',
        fontStyle: '800',
        color: TILE_COLORS.text
      }).setOrigin(0.5);
      const container = this.add.container(base.x, base.y, [bg, text]).setDepth(8);
      const zone = this.add.zone(base.x, base.y, tileW, tileH)
        .setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => this.onTileClick(i));
      this.tileBasePositions.set(i, base);
      this.tileContainers[i] = container;
      this.tileBgs[i] = bg;
      this.tileTexts[i] = text;
      this.tileZones[i] = zone;
      this.paintTile(i);
    }
  }

  private drawFullDoneMessage() {
    this.instructionText = this.add.text(
      this.boardOriginX + (9 * this.cellSize) / 2,
      FULL_SUDOKU_INPUT_LAYOUT.instructionY,
      'Sudoku complete.',
      {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        fontStyle: '800',
        color: '#166534'
      }
    ).setOrigin(0.5);
  }

  private onTileClick(tileIndex: number) {
    if (this.mode === 'full') {
      this.onNumberClick(tileIndex + 1);
      return;
    }

    if (this.completed || this.isKey9InputLocked() || this.usedTiles.has(tileIndex)) return;
    this.flashTileButton(tileIndex);
    const decision = resolveKey9TileTap(this.puzzle, {
      misses: this.misses,
      maxMisses: 9,
      lockedCells: this.lockedCells,
      usedTiles: this.usedTiles
    }, tileIndex, this.selectedCellIndex);

    if (decision.kind === 'needs_cell') {
      this.selectedTileIndex = null;
      this.highlightSelectedTile();
      this.refreshBoardHighlights();
      this.pulseTileTray();
      this.setInstruction(decision.prompt);
      return;
    }

    if (decision.kind === 'ignored') {
      this.selectedTileIndex = null;
      this.highlightSelectedTile();
      this.refreshBoardHighlights();
      this.setInstruction(decision.reason === 'locked_cell' ? 'Select another marked cell.' : 'Select a marked cell first.');
      return;
    }

    this.selectedTileIndex = null;
    this.highlightSelectedTile();
    this.tryPlace(decision.cellIndex, decision.tileIndex);
  }

  private onCellClick(cellIndex: number) {
    if (this.mode === 'full') {
      this.onFullCellClick(cellIndex);
      return;
    }

    if (this.completed || this.isKey9InputLocked()) return;

    if (!this.keySet.has(cellIndex)) {
      this.flashCell(cellIndex, BOARD_COLORS.subtleFill, 120);
      return;
    }

    if (this.lockedCells.has(cellIndex)) {
      this.selectedCellIndex = cellIndex;
      this.selectedTileIndex = null;
      this.refreshBoardHighlights();
      this.flashCell(cellIndex, BOARD_COLORS.successFill, 120);
      this.setInstruction('This Key9 cell is locked.');
      return;
    }

    this.selectedCellIndex = cellIndex;
    this.selectedTileIndex = null;
    this.highlightSelectedTile();
    this.refreshBoardHighlights();
    this.setInstruction('Now place a 1–9 tile.');
  }

  private tryPlace(cellIndex: number, tileIndex: number) {
    if (!this.beginKey9Placement()) return;
    this.resetTileTrayVisualState();
    const beforeBoard = this.getCurrentBoardValues();

    const result = applyKey9Attempt(this.puzzle, {
      misses: this.misses,
      maxMisses: 9,
      lockedCells: this.lockedCells,
      usedTiles: this.usedTiles
    }, cellIndex, tileIndex);

    if (result.kind === 'ignored') {
      this.scheduleKey9InputUnlock(0);
      return;
    }

    this.misses = result.state.misses;
    this.lockedCells = result.state.lockedCells;
    this.usedTiles = result.state.usedTiles;

    if (result.kind === 'correct') {
      this.undoStack.push({ cell: cellIndex, tileIndex, digit: result.digit });
      this.drawLockedNumber(cellIndex, result.digit);
      this.redrawTile(tileIndex);
      const afterBoard = this.getCurrentBoardValues();
      const completedUnits = detectNewlyCompletedUnits({ beforeBoard, afterBoard, placedCellIndex: cellIndex });
      this.selectedTileIndex = null;
      this.selectedCellIndex = null;
      this.highlightSelectedTile();
      this.refreshBoardHighlights();
      this.setInstruction(result.completed ? 'Key9 unlocked.' : 'Nice. Pick the next digit.');
      this.emitStatus();
      this.resetTileTrayVisualState();
      if (result.completed) {
        this.complete();
        this.playUnitCompletionEffects(completedUnits);
        this.playKey9UnlockCelebration(getKey9UnlockCelebration({
          keyCells: this.puzzle.keyCells,
          usedTileCount: this.usedTiles.size,
          totalTiles: this.puzzle.keyTiles.length
        }));
        return;
      }
      this.playUnitCompletionEffects(completedUnits);
      this.scheduleKey9InputUnlock(240);
    } else {
      this.showWrongCellFeedback(cellIndex, { shake: !result.missed });
      this.resetTileTrayVisualState();
      this.setInstruction(result.missed ? 'Missed.' : 'Try another marked cell.');
      this.emitStatus();
      if (result.missed) {
        this.cancelKey9FeedbackUnlock();
        this.enterKey9TerminalInputState();
        this.resetTileTrayVisualState();
        const token = this.key9FeedbackToken;
        this.time.delayedCall(120, () => {
          if (token !== this.key9FeedbackToken) return;
          this.cleanupBoardFeedback({ terminal: true });
          this.resetTileTrayVisualState();
          this.missOut();
        });
        return;
      }
      this.scheduleKey9InputUnlock(420);
    }
  }

  private beginKey9Placement(): boolean {
    const next = beginKey9PlacementAttempt(this.key9InputGate);
    this.key9InputGate = next.state;
    return next.accepted;
  }

  private isKey9InputLocked(): boolean {
    return this.key9InputGate.inputLocked;
  }

  private scheduleKey9InputUnlock(delay: number) {
    this.key9FeedbackToken += 1;
    const token = this.key9FeedbackToken;
    this.key9FeedbackEvent?.remove(false);
    this.key9FeedbackEvent = this.time.delayedCall(delay, () => {
      if (token !== this.key9FeedbackToken) return;
      this.key9FeedbackEvent = undefined;
      if (this.mode !== 'key9' || this.completed) return;
      this.resetTileTrayVisualState();
      this.key9InputGate = finishKey9PlacementFeedback(this.key9InputGate);
    });
  }

  private cancelKey9FeedbackUnlock() {
    this.key9FeedbackToken += 1;
    this.key9FeedbackEvent?.remove(false);
    this.key9FeedbackEvent = undefined;
  }

  private enterKey9TerminalInputState() {
    this.key9InputGate = enterKey9TerminalState(this.key9InputGate);
  }

  private drawLockedNumber(cellIndex: number, digit: number) {
    const r = Math.floor(cellIndex / 9);
    const c = cellIndex % 9;
    const x = this.boardOriginX + c * this.cellSize + this.cellSize / 2;
    const y = this.boardOriginY + r * this.cellSize + this.cellSize / 2;
    const rect = this.cellRects.get(cellIndex);
    rect?.setFillStyle(BOARD_COLORS.successFlash);
    rect?.setStrokeStyle(2, BOARD_COLORS.successBorder);
    this.time.delayedCall(180, () => {
      rect?.setFillStyle(BOARD_COLORS.successFill);
      rect?.setStrokeStyle(2, BOARD_COLORS.successBorder);
    });
    const existing = this.cellTexts.get(cellIndex);
    existing?.destroy();
    const txt = this.add.text(x, y, String(digit), {
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      fontStyle: '800',
      color: BOARD_COLORS.successText
    }).setOrigin(0.5);
    txt.setDepth(6);
    this.cellTexts.set(cellIndex, txt);
    this.refreshKey9Marker(cellIndex);
  }

  private redrawKeyCell(cellIndex: number) {
    const rect = this.cellRects.get(cellIndex);
    rect?.setFillStyle(BOARD_COLORS.keyFill);
    rect?.setStrokeStyle(1.5, BOARD_COLORS.keyBorder, 0.9);
    const txt = this.cellTexts.get(cellIndex);
    txt?.destroy();
    this.cellTexts.delete(cellIndex);
    this.refreshKey9Marker(cellIndex);
  }

  private redrawTile(tileIndex: number) {
    this.paintTile(tileIndex);
  }

  private highlightSelectedTile() {
    this.tileContainers.forEach((_, i) => this.paintTile(i));
  }

  private refreshBoardHighlights() {
    this.applyBoardHighlightsToRects(this.getBoardHighlights());
  }

  private refreshBoardVisualState() {
    this.refreshFullConflictState();
    this.refreshBoardHighlights();
    this.refreshCellTextStyles();
  }

  private refreshFullConflictState() {
    if (this.mode !== 'full') {
      this.conflictingFullCells.clear();
      return;
    }

    this.conflictingFullCells = new Set(getFullSudokuConflicts(
      this.puzzle,
      this.fullBoard,
      this.fullLockedCells
    ).conflictingUserCells);
  }

  private refreshCellTextStyles() {
    this.cellTexts.forEach((text, cellIndex) => {
      text.setColor(this.getCellTextColor(cellIndex));
    });
  }

  private getBoardHighlights(): BoardHighlightTargets {
    return getBoardHighlightTargets({
      board: this.getCurrentBoardValues(),
      selectedCellIndex: this.completed ? null : this.selectedCellIndex,
      selectedDigit: this.completed ? null : this.getSelectedDigit()
    });
  }

  private getSelectedDigit(): number | null {
    if (this.mode === 'key9' && this.selectedTileIndex != null) {
      return this.puzzle.keyTiles[this.selectedTileIndex] ?? null;
    }
    if (this.mode === 'full') return this.selectedNumber;
    return null;
  }

  private getCurrentBoardValues(): string[] {
    return Array.from({ length: 81 }, (_, cellIndex) => this.getCellValue(cellIndex));
  }

  private applyBoardHighlightsToRects(targets: BoardHighlightTargets) {
    const peerCells = new Set([...targets.rowCells, ...targets.columnCells, ...targets.boxCells]);
    const sameDigitCells = new Set(targets.sameDigitCells);

    this.cellRects.forEach((rect, cellIndex) => {
      const selected = targets.selectedCellIndex === cellIndex;
      const sameDigit = sameDigitCells.has(cellIndex);
      const peer = peerCells.has(cellIndex);
      rect.setFillStyle(this.getHighlightedCellFill(cellIndex, { selected, sameDigit, peer }));
      this.paintCellStroke(cellIndex, { selected, sameDigit });
    });
    this.stabilizeKey9CellMarkers();
  }

  private getHighlightedCellFill(cellIndex: number, highlight: { selected: boolean; sameDigit: boolean; peer: boolean }): number {
    const baseFill = this.getCellFill(cellIndex);
    const keyLocked = this.lockedCells.has(cellIndex) || this.fullLockedCells.has(cellIndex);
    const keyMarked = this.keySet.has(cellIndex);

    if (highlight.selected) return keyLocked ? BOARD_COLORS.lockedKeyFill : BOARD_COLORS.selectedFill;
    if (this.mode === 'full' && this.conflictingFullCells.has(cellIndex)) return BOARD_COLORS.conflictFill;
    if (highlight.sameDigit) return keyLocked ? BOARD_COLORS.lockedKeyFill : BOARD_COLORS.sameDigitFill;
    if (highlight.peer && !keyMarked) return BOARD_COLORS.peerHighlightFill;
    return baseFill;
  }

  private paintTile(tileIndex: number) {
    const bg = this.tileBgs[tileIndex];
    const text = this.tileTexts[tileIndex];
    const container = this.tileContainers[tileIndex];
    const zone = this.tileZones[tileIndex];
    if (!bg || !text || !container || !zone) return;

    const used = this.mode === 'key9' && this.usedTiles.has(tileIndex);
    const selected = this.mode === 'full'
      ? this.selectedNumber === tileIndex + 1
      : this.selectedTileIndex === tileIndex && !used;
    const tileW = 38;
    const tileH = 42;
    const radius = 8;

    bg.clear();
    if (selected) {
      bg.fillStyle(TILE_COLORS.shadow, 0.14);
      bg.fillRoundedRect(-tileW / 2 + 2, -tileH / 2 + 4, tileW, tileH, radius);
    }
    bg.fillStyle(used ? TILE_COLORS.usedFill : selected ? TILE_COLORS.selectedFill : TILE_COLORS.fill, used ? 0.8 : 1);
    bg.fillRoundedRect(-tileW / 2, -tileH / 2, tileW, tileH, radius);
    bg.lineStyle(selected ? 3 : 2, used ? TILE_COLORS.usedBorder : selected ? TILE_COLORS.selectedBorder : TILE_COLORS.border, 1);
    bg.strokeRoundedRect(-tileW / 2, -tileH / 2, tileW, tileH, radius);

    text.setColor(used ? TILE_COLORS.usedText : selected ? TILE_COLORS.selectedText : TILE_COLORS.text);
    text.setAlpha(used ? 0.38 : 1);
    container.setScale(selected ? 1.08 : 1);

    if (used) {
      zone.disableInteractive();
    } else if (!zone.input?.enabled) {
      zone.setInteractive({ useHandCursor: true });
    }

    this.paintTileDoneMarker(tileIndex, used);
  }

  private paintTileDoneMarker(tileIndex: number, used: boolean) {
    const container = this.tileContainers[tileIndex];
    if (!container) return;
    const existing = container.getByName('key9-tile-done') as Phaser.GameObjects.Text | null;
    if (existing) container.remove(existing, true);
    if (!used || this.mode !== 'key9') return;

    const done = this.add.text(11, -12, '✓', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      fontStyle: '900',
      color: '#15803d'
    }).setOrigin(0.5);
    done.setName('key9-tile-done');
    container.add(done);
  }

  private showWrongCellFeedback(cellIndex: number, options: { shake?: boolean } = {}) {
    this.clearCellFeedbackEffects();
    this.stabilizeBoardVisualState({ keepSelectedOutline: true });
    const base = this.getCellBaseRect(cellIndex);
    const overlay = this.add.rectangle(base.centerX, base.centerY, base.width - 2, base.height - 2, BOARD_COLORS.wrongFill, 0.82);
    overlay.setDepth(4.8);
    overlay.setStrokeStyle(2, BOARD_COLORS.wrongBorder, 0.9);
    this.cellFeedbackOverlays.add(overlay);

    if (options.shake === false) {
      this.time.delayedCall(110, () => this.destroyCellFeedbackOverlay(overlay));
      return;
    }

    this.tweens.add({
      targets: overlay,
      x: { from: base.centerX - 4, to: base.centerX + 4 },
      duration: 34,
      repeat: 2,
      yoyo: true,
      onComplete: () => {
        overlay.setPosition(base.centerX, base.centerY);
        this.destroyCellFeedbackOverlay(overlay);
        this.stabilizeBoardVisualState({ keepSelectedOutline: this.mode === 'key9' && !this.completed });
      }
    });
  }

  private flashCell(cellIndex: number, color: number, duration = 650) {
    this.clearCellFeedbackEffects();
    const base = this.getCellBaseRect(cellIndex);
    const overlay = this.add.rectangle(base.centerX, base.centerY, base.width - 2, base.height - 2, color, 0.28);
    overlay.setDepth(4.8);
    this.cellFeedbackOverlays.add(overlay);
    this.time.delayedCall(duration, () => this.destroyCellFeedbackOverlay(overlay));
  }

  private playUnitCompletionEffects(units: CompletedUnit[]) {
    units.forEach((unit, index) => {
      this.time.delayedCall(index * 70, () => this.playUnitCompletionEffect(unit));
    });
  }

  private playUnitCompletionEffect(unit: CompletedUnit) {
    const bounds = this.getUnitBounds(unit.cells);
    const overlay = this.add.rectangle(
      bounds.centerX,
      bounds.centerY,
      bounds.width - 3,
      bounds.height - 3,
      unit.type === 'box' ? BOARD_COLORS.unlockFill : BOARD_COLORS.completionFill,
      unit.type === 'box' ? 0.16 : 0.22
    );
    overlay.setDepth(4.2);
    overlay.setStrokeStyle(unit.type === 'box' ? 2.5 : 1.5, BOARD_COLORS.completionBorder, unit.type === 'box' ? 0.85 : 0.28);
    this.cellFeedbackOverlays.add(overlay);
    this.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: unit.type === 'box' ? 430 : 340,
      ease: 'Sine.easeOut',
      onComplete: () => this.destroyCellFeedbackOverlay(overlay)
    });
  }

  private playKey9UnlockCelebration(celebration: { play: boolean; cells: number[]; tileIndices: number[] }) {
    if (!celebration.play) return;

    const boardGlow = this.add.rectangle(
      this.boardOriginX + (9 * this.cellSize) / 2,
      this.boardOriginY + (9 * this.cellSize) / 2,
      9 * this.cellSize - 8,
      9 * this.cellSize - 8,
      BOARD_COLORS.unlockFill,
      0.08
    );
    boardGlow.setDepth(4.1);
    boardGlow.setStrokeStyle(4, BOARD_COLORS.successBorder, 0.2);
    this.cellFeedbackOverlays.add(boardGlow);
    this.tweens.add({
      targets: boardGlow,
      alpha: 0,
      duration: 620,
      ease: 'Sine.easeOut',
      onComplete: () => this.destroyCellFeedbackOverlay(boardGlow)
    });

    celebration.cells.forEach((cellIndex, index) => {
      this.time.delayedCall(index * 32, () => {
        const base = this.getCellBaseRect(cellIndex);
        const overlay = this.add.rectangle(base.centerX, base.centerY, base.width - 4, base.height - 4, BOARD_COLORS.unlockFill, 0.24);
        overlay.setDepth(4.2);
        overlay.setStrokeStyle(2, BOARD_COLORS.successBorder, 0.32);
        this.cellFeedbackOverlays.add(overlay);
        this.tweens.add({
          targets: overlay,
          alpha: 0,
          duration: 360,
          ease: 'Sine.easeOut',
          onComplete: () => this.destroyCellFeedbackOverlay(overlay)
        });
      });
    });

    celebration.tileIndices.forEach((tileIndex) => {
      const base = this.tileBasePositions.get(tileIndex);
      if (!base) return;
      const glow = this.add.graphics();
      glow.setDepth(8.4);
      glow.lineStyle(2.5, BOARD_COLORS.successBorder, 0.86);
      glow.strokeRoundedRect(base.x - 19, base.y - 21, 38, 42, 8);
      glow.setAlpha(0);
      this.cellFeedbackOverlays.add(glow);
      this.tweens.add({
        targets: glow,
        alpha: { from: 0, to: 0.8 },
        duration: 160,
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: () => this.destroyCellFeedbackOverlay(glow)
      });
    });
  }

  private playFullCompletionCelebration() {
    const centerX = this.boardOriginX + (9 * this.cellSize) / 2;
    const centerY = this.boardOriginY + (9 * this.cellSize) / 2;
    const size = 9 * this.cellSize;

    const glow = this.add.rectangle(centerX, centerY, size - 8, size - 8, BOARD_COLORS.unlockFill, 0.12);
    glow.setDepth(4.1);
    glow.setStrokeStyle(4, BOARD_COLORS.successBorder, 0.24);
    this.cellFeedbackOverlays.add(glow);
    this.tweens.add({
      targets: glow,
      alpha: 0,
      duration: 720,
      ease: 'Sine.easeOut',
      onComplete: () => this.destroyCellFeedbackOverlay(glow)
    });

    const sweep = this.add.rectangle(
      this.boardOriginX + this.cellSize / 2,
      centerY,
      this.cellSize * 1.2,
      size - 12,
      BOARD_COLORS.completionFill,
      0.18
    );
    sweep.setDepth(4.15);
    this.cellFeedbackOverlays.add(sweep);
    this.tweens.add({
      targets: sweep,
      x: this.boardOriginX + size - this.cellSize / 2,
      alpha: 0,
      duration: 620,
      ease: 'Sine.easeInOut',
      onComplete: () => this.destroyCellFeedbackOverlay(sweep)
    });
  }

  private flashTileButton(tileIndex: number) {
    const base = this.tileBasePositions.get(tileIndex);
    if (!base) return;

    const overlay = this.add.graphics();
    overlay.setDepth(8.35);
    overlay.fillStyle(TILE_COLORS.selectedFill, 0.34);
    overlay.fillRoundedRect(base.x - 19, base.y - 21, 38, 42, 8);
    overlay.lineStyle(2, TILE_COLORS.selectedBorder, 0.5);
    overlay.strokeRoundedRect(base.x - 19, base.y - 21, 38, 42, 8);
    this.cellFeedbackOverlays.add(overlay);
    this.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: 150,
      ease: 'Sine.easeOut',
      onComplete: () => this.destroyCellFeedbackOverlay(overlay)
    });
  }

  private getUnitBounds(cells: number[]) {
    const rows = cells.map((cell) => Math.floor(cell / 9));
    const cols = cells.map((cell) => cell % 9);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    const left = this.boardOriginX + minCol * this.cellSize;
    const top = this.boardOriginY + minRow * this.cellSize;
    const width = (maxCol - minCol + 1) * this.cellSize;
    const height = (maxRow - minRow + 1) * this.cellSize;
    return {
      centerX: left + width / 2,
      centerY: top + height / 2,
      width,
      height
    };
  }

  private pulseTileTray() {
    const targets = this.tileContainers.filter((_, i) => !this.usedTiles.has(i));
    this.tweens.add({
      targets,
      scale: 1.06,
      duration: 95,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
      onComplete: () => this.highlightSelectedTile()
    });
  }

  private setInstruction(message: string) {
    this.instructionText?.setText(message);
    if (this.classic) this.instructionText?.setStyle({ fontSize: '13px', fontStyle: '500', color: this.completed ? '#166534' : '#647181' });
    if (this.classic) window.dispatchEvent(new CustomEvent('daysudoku:instruction', { detail: { message } }));
  }

  private clearTray() {
    this.resetTileTrayVisualState({ repaint: false });
    this.tileZones.forEach((zone) => zone.destroy());
    this.tileContainers.forEach((container) => container.destroy());
    this.tileBgs = [];
    this.tileTexts = [];
    this.tileContainers = [];
    this.tileZones = [];
    this.tileBasePositions.clear();
    this.instructionText?.destroy();
    this.trayLabel?.destroy();
    this.instructionText = undefined;
    this.trayLabel = undefined;
  }

  private resetTileVisualPosition(tileIndex: number, options: { killTweens?: boolean; repaint?: boolean } = {}) {
    const container = this.tileContainers[tileIndex];
    const base = this.tileBasePositions.get(tileIndex);
    if (!container) return;
    if (options.killTweens !== false) this.tweens.killTweensOf(container);
    if (!base) return;
    const reset = resetTileVisualSnapshot({
      x: container.x,
      y: container.y,
      scale: container.scaleX,
      baseX: base.x,
      baseY: base.y
    });
    container.setPosition(reset.x, reset.y);
    container.setScale(reset.scale);
    if (options.repaint !== false) this.paintTile(tileIndex);
  }

  private resetTileTrayVisualState(options: { repaint?: boolean } = {}) {
    this.tileContainers.forEach((container, tileIndex) => {
      if (!container) return;
      this.resetTileVisualPosition(tileIndex, { repaint: options.repaint });
    });
  }

  private getCellBaseRect(cellIndex: number) {
    return getCellBaseRect({
      cellIndex,
      boardOriginX: this.boardOriginX,
      boardOriginY: this.boardOriginY,
      cellSize: this.cellSize
    });
  }

  private destroyCellFeedbackOverlay(overlay: Phaser.GameObjects.GameObject) {
    if (!this.cellFeedbackOverlays.has(overlay)) return;
    this.tweens.killTweensOf(overlay);
    this.cellFeedbackOverlays.delete(overlay);
    overlay.destroy();
  }

  private clearCellFeedbackEffects() {
    this.cellFeedbackOverlays.forEach((overlay) => {
      this.tweens.killTweensOf(overlay);
      overlay.destroy();
    });
    this.cellFeedbackOverlays.clear();
  }

  private stabilizeBoardVisualState(options: { keepSelectedOutline?: boolean } = {}) {
    this.cellRects.forEach((rect, cellIndex) => {
      const base = this.getCellBaseRect(cellIndex);
      const reset = resetBoardVisualSnapshot({
        x: rect.x,
        y: rect.y,
        baseX: base.centerX,
        baseY: base.centerY
      });
      this.tweens.killTweensOf(rect);
      rect.setPosition(reset.x, reset.y);
      rect.setSize(base.width, base.height);
      rect.setFillStyle(this.getCellFill(cellIndex));
      this.paintCellStroke(cellIndex);
    });

    this.stabilizeKey9CellMarkers();
    if (options.keepSelectedOutline) this.refreshBoardHighlights();
  }

  private stabilizeKey9CellMarkers() {
    this.puzzle.keyCells.forEach((cellIndex) => this.refreshKey9Marker(cellIndex));
  }

  private cleanupBoardFeedback(options: { terminal: boolean }) {
    const cleanup = getBoardFeedbackCleanup({
      activeFeedbackCount: this.cellFeedbackOverlays.size,
      selectedCellIndex: this.selectedCellIndex,
      terminal: options.terminal
    });

    if (cleanup.activeFeedbackCount === 0) this.clearCellFeedbackEffects();
    if (cleanup.selectedCellIndex == null) this.selectedCellIndex = null;
    if (cleanup.resetCellGeometry || cleanup.resetMarkers || cleanup.resetOutlines) {
      this.stabilizeBoardVisualState({ keepSelectedOutline: false });
    }
  }

  private prepareFullMode(savedBoard?: string[], savedNotes?: string[], savedFullHints = 0) {
    const keyLocks = this.classic ? new Map<number, number>() : this.officialKey9Missed
      ? new Map(this.lockedCells)
      : new Map(this.puzzle.keyCells.map((cell) => [cell, Number(this.puzzle.solution[cell])]));
    this.fullLockedCells = new Set(keyLocks.keys());
    this.lockedCells = keyLocks;
    this.fullBoard = savedBoard?.length === 81 ? [...savedBoard] : createFullBoard(this.puzzle, this.lockedCells);
    this.fullNotes = createFullNotes(savedNotes);
    this.fullHints = Math.max(0, Math.floor(savedFullHints));
    this.lockedCells.forEach((digit, cell) => {
      this.fullBoard[cell] = String(digit);
      this.fullNotes[cell] = '';
    });
    this.refreshFullConflictState();
  }

  private getCellValue(cellIndex: number): string {
    if (this.mode === 'full') return this.fullBoard[cellIndex] ?? this.puzzle.puzzle[cellIndex] ?? '0';
    return this.lockedCells.has(cellIndex) ? String(this.lockedCells.get(cellIndex)) : this.puzzle.puzzle[cellIndex];
  }

  private getCellFill(cellIndex: number): number {
    if (this.mode === 'full') {
      if (this.fullLockedCells.has(cellIndex)) return BOARD_COLORS.lockedKeyFill;
      return this.puzzle.puzzle[cellIndex] === '0' ? BOARD_COLORS.emptyCell : BOARD_COLORS.givenCell;
    }
    if (this.lockedCells.has(cellIndex)) return BOARD_COLORS.successFill;
    return this.keySet.has(cellIndex)
      ? BOARD_COLORS.keyFill
      : this.puzzle.puzzle[cellIndex] === '0'
        ? BOARD_COLORS.emptyCell
        : BOARD_COLORS.givenCell;
  }

  private getCellTextColor(cellIndex: number): string {
    if (this.mode === 'full') {
      if (this.conflictingFullCells.has(cellIndex)) return BOARD_COLORS.conflictText;
      if (this.fullLockedCells.has(cellIndex)) return BOARD_COLORS.lockedKeyText;
      if (this.puzzle.puzzle[cellIndex] === '0') return BOARD_COLORS.userText;
    }
    return BOARD_COLORS.givenText;
  }

  private isCellActionable(cellIndex: number): boolean {
    if (this.mode === 'full') return this.puzzle.puzzle[cellIndex] === '0' && !this.fullLockedCells.has(cellIndex);
    return this.keySet.has(cellIndex);
  }

  private paintCellStroke(cellIndex: number, highlight: { selected?: boolean; sameDigit?: boolean } = {}) {
    const rect = this.cellRects.get(cellIndex);
    if (!rect) return;

    if (highlight.selected) {
      rect.setStrokeStyle(2.5, BOARD_COLORS.keyBorderActive, 0.95);
      return;
    }

    if (highlight.sameDigit) {
      rect.setStrokeStyle(2, BOARD_COLORS.keyBorderActive, 0.72);
      return;
    }

    if (this.mode === 'full' && this.conflictingFullCells.has(cellIndex)) {
      rect.setStrokeStyle(2, BOARD_COLORS.conflictBorder, 0.72);
      return;
    }

    if (this.mode === 'full' && this.fullLockedCells.has(cellIndex)) {
      rect.setStrokeStyle(1.5, BOARD_COLORS.lockedKeyBorder, 0.75);
      return;
    }

    if (this.mode === 'key9' && this.keySet.has(cellIndex)) {
      rect.setStrokeStyle(1.5, BOARD_COLORS.keyBorder, 0.9);
      return;
    }

    rect.setStrokeStyle(0, BOARD_COLORS.thinLine, 0);
  }

  private repaintBoard() {
    this.clearCellFeedbackEffects();
    this.refreshFullConflictState();
    this.cellRects.forEach((rect, cellIndex) => {
      const base = this.getCellBaseRect(cellIndex);
      rect.setPosition(base.centerX, base.centerY);
      rect.setSize(base.width, base.height);
      rect.setFillStyle(this.getCellFill(cellIndex));
      rect.input!.cursor = this.mode === 'full' || this.isCellActionable(cellIndex) ? 'pointer' : 'default';
      this.paintCellStroke(cellIndex);
      this.updateCellText(cellIndex, this.getCellValue(cellIndex), this.getCellTextColor(cellIndex), this.fullLockedCells.has(cellIndex) ? '800' : '700');
      this.updateCellNotes(cellIndex);
      this.refreshKey9Marker(cellIndex);
    });
    this.refreshBoardVisualState();
  }

  private updateCellText(cellIndex: number, value: string, color: string, fontStyle = '700') {
    const existing = this.cellTexts.get(cellIndex);
    existing?.destroy();
    this.cellTexts.delete(cellIndex);
    if (value !== '0') this.clearCellNotes(cellIndex);
    if (value === '0') return;

    const base = this.getCellBaseRect(cellIndex);
    const txt = this.add.text(base.centerX, base.centerY, value, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      fontStyle: this.classic ? '500' : fontStyle,
      color
    }).setOrigin(0.5);
    txt.setDepth(6);
    this.cellTexts.set(cellIndex, txt);
  }

  private updateCellNotes(cellIndex: number) {
    this.clearCellNotes(cellIndex);
    if (this.mode !== 'full' || this.fullBoard[cellIndex] !== '0') return;
    const notes = this.fullNotes[cellIndex] ?? '';
    if (!notes) return;

    const base = this.getCellBaseRect(cellIndex);
    const noteObjects: Phaser.GameObjects.Text[] = [];
    for (const char of notes) {
      const digit = Number(char);
      if (digit < 1 || digit > 9) continue;
      const noteRow = Math.floor((digit - 1) / 3);
      const noteCol = (digit - 1) % 3;
      const x = base.left + (noteCol + 0.5) * (this.cellSize / 3);
      const y = base.top + (noteRow + 0.5) * (this.cellSize / 3);
      const note = this.add.text(x, y, char, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '9px',
        fontStyle: '700',
        color: '#64748b'
      }).setOrigin(0.5);
      note.setDepth(6);
      noteObjects.push(note);
    }
    if (noteObjects.length) this.cellNoteTexts.set(cellIndex, noteObjects);
  }

  private clearCellNotes(cellIndex: number) {
    this.cellNoteTexts.get(cellIndex)?.forEach((note) => note.destroy());
    this.cellNoteTexts.delete(cellIndex);
  }

  private onNumberClick(digit: number) {
    if (this.completed) return;

    this.flashTileButton(digit - 1);
    const selectedCellIndex = this.selectedCellIndex;
    const decision = resolveFullNumberTap(this.puzzle, {
      board: this.fullBoard,
      notes: this.fullNotes,
      lockedCells: this.fullLockedCells,
      selectedCellIndex,
      digit,
      notesMode: this.notesMode
    });

    this.selectedNumber = null;
    this.highlightSelectedTile();

    if (decision.kind === 'needs_cell' || decision.kind === 'ignored') {
      this.pulseTileTray();
      this.refreshBoardVisualState();
      this.setInstruction(
        selectedCellIndex != null && this.isCellActionable(selectedCellIndex)
          ? 'That entry is already set.'
          : 'Select an empty cell first.'
      );
      return;
    }

    if (selectedCellIndex == null) return;

    if (decision.kind === 'noted') {
      this.fullUndoStack.push({ cell: selectedCellIndex, previous: this.fullBoard[selectedCellIndex], next: this.fullBoard[selectedCellIndex], previousNotes: this.fullNotes[selectedCellIndex] });
      this.fullNotes = decision.notes;
      this.updateCellNotes(selectedCellIndex);
      this.refreshBoardVisualState();
      this.setInstruction('Note updated.');
      this.emitFullProgress();
      this.emitStatus();
      return;
    }

    this.applyFullNumberPlacement(selectedCellIndex, decision.board);
  }

  enterDigit(digit: number) {
    if (this.mode === 'full' && Number.isInteger(digit) && digit >= 1 && digit <= 9) this.onNumberClick(digit);
  }

  selectFullCell(cellIndex: number) {
    if (this.mode !== 'full' || !Number.isInteger(cellIndex) || cellIndex < 0 || cellIndex > 80) return;
    this.onFullCellClick(cellIndex);
  }

  moveSelection(rowDelta: number, columnDelta: number) {
    if (this.mode !== 'full' || this.completed) return;
    const cell = this.selectedCellIndex ?? 0;
    const row = Math.min(8, Math.max(0, Math.floor(cell / 9) + rowDelta));
    const col = Math.min(8, Math.max(0, cell % 9 + columnDelta));
    this.onFullCellClick(row * 9 + col);
  }

  private onFullCellClick(cellIndex: number) {
    if (this.completed) return;

    this.selectedCellIndex = cellIndex;
    this.selectedNumber = null;
    this.highlightSelectedTile();
    this.refreshBoardVisualState();

    if (!this.isCellActionable(cellIndex)) {
      this.setInstruction('Matching numbers highlighted.');
      this.emitStatus();
      return;
    }

    this.setInstruction(this.notesMode ? 'Now enter candidate notes.' : 'Now enter a number.');
    this.emitStatus();
  }

  private applyFullNumberPlacement(cellIndex: number, nextBoard: string[]) {
    const previous = this.fullBoard[cellIndex] ?? '0';
    const previousNotes = this.fullNotes[cellIndex];
    const beforeBoard = [...this.fullBoard];
    this.fullBoard = nextBoard;
    this.fullNotes = clearFullNotesForCell(this.fullNotes, cellIndex);
    this.fullUndoStack.push({ cell: cellIndex, previous, next: this.fullBoard[cellIndex], previousNotes });
    this.updateCellText(cellIndex, this.fullBoard[cellIndex], this.getCellTextColor(cellIndex));
    this.updateCellNotes(cellIndex);
    this.refreshBoardVisualState();
    const completedUnits = detectNewlyCompletedUnits({ beforeBoard, afterBoard: this.fullBoard, placedCellIndex: cellIndex });
    this.emitFullProgress();
    this.emitStatus();

    if (isFullSudokuComplete(this.puzzle, this.fullBoard)) {
      this.completeFullSudoku();
      this.playUnitCompletionEffects(completedUnits);
      return;
    }
    this.playUnitCompletionEffects(completedUnits);
  }

  private toggleFullCellNote(cellIndex: number, digit: number) {
    const result = toggleFullNote(this.puzzle, this.fullBoard, this.fullLockedCells, this.fullNotes, cellIndex, digit);
    if (!result.changed) {
      this.flashCell(cellIndex, BOARD_COLORS.subtleFill, 130);
      return;
    }

    this.fullNotes = result.notes;
    this.updateCellNotes(cellIndex);
    this.refreshBoardVisualState();
    this.emitFullProgress();
    this.emitStatus();
  }

  private undoFullMove() {
    if (this.completed) return;
    const last = this.fullUndoStack.pop();
    if (!last) return;
    this.fullBoard[last.cell] = last.previous;
    this.fullNotes[last.cell] = last.previousNotes ?? '';
    this.updateCellText(last.cell, last.previous, BOARD_COLORS.userText);
    this.updateCellNotes(last.cell);
    this.refreshBoardVisualState();
    this.emitFullProgress();
    this.emitStatus();
  }

  private eraseFullCell() {
    if (this.completed || this.selectedCellIndex == null) return;
    const cell = this.selectedCellIndex;
    if (!this.isCellActionable(cell)) return;
    const previous = this.fullBoard[cell];
    const previousNotes = this.fullNotes[cell];
    const result = eraseFullCellEntry(this.puzzle, this.fullBoard, this.fullLockedCells, this.fullNotes, cell);
    if (!result.changed) return;
    this.fullBoard = result.board;
    this.fullNotes = result.notes;
    this.fullUndoStack.push({ cell, previous, next: '0', previousNotes });
    this.updateCellText(cell, '0', BOARD_COLORS.userText);
    this.updateCellNotes(cell);
    this.refreshBoardVisualState();
    this.emitFullProgress();
    this.emitStatus();
  }

  private hintFullCell() {
    if (this.completed) return;
    const beforeBoard = [...this.fullBoard];
    const previousNotes = this.selectedCellIndex == null ? '' : this.fullNotes[this.selectedCellIndex];
    const result = applyFullHint(this.puzzle, this.fullBoard, this.fullLockedCells, this.fullNotes, this.selectedCellIndex, this.fullHints);
    if (!result.changed) {
      this.setInstruction(result.prompt);
      this.emitStatus();
      return;
    }
    this.fullBoard = result.board;
    this.fullNotes = result.notes;
    this.fullHints = result.fullHints;
    this.fullUndoStack.push({ cell: result.cellIndex, previous: result.previous, next: result.next, previousNotes });
    this.updateCellText(result.cellIndex, this.fullBoard[result.cellIndex], BOARD_COLORS.userText);
    this.updateCellNotes(result.cellIndex);
    this.refreshBoardVisualState();
    this.flashCell(result.cellIndex, BOARD_COLORS.successFill, 220);
    const completedUnits = detectNewlyCompletedUnits({ beforeBoard, afterBoard: this.fullBoard, placedCellIndex: result.cellIndex });
    this.setInstruction('Hint filled this cell.');
    this.emitFullProgress();
    this.emitStatus();
    if (result.completed) {
      this.completeFullSudoku();
      this.playUnitCompletionEffects(completedUnits);
      return;
    }
    this.playUnitCompletionEffects(completedUnits);
  }

  private emitFullProgress() {
    window.dispatchEvent(new CustomEvent('daysudoku:full-progress', {
      detail: { board: [...this.fullBoard], notes: [...this.fullNotes], fullHints: this.fullHints }
    }));
  }

  private completeFullSudoku() {
    this.completed = true;
    this.cleanupBoardFeedback({ terminal: true });
    this.resetTileTrayVisualState();
    this.drawTiles();
    this.playFullCompletionCelebration();
    this.setInstruction('Sudoku complete.');
    const seconds = Math.max(1, Math.round((Date.now() - this.startMs) / 1000));
    window.dispatchEvent(new CustomEvent('daysudoku:full-complete', {
      detail: { board: [...this.fullBoard], notes: [...this.fullNotes], fullHints: this.fullHints, seconds }
    }));
  }

  private complete() {
    this.completed = true;
    this.cancelKey9FeedbackUnlock();
    this.enterKey9TerminalInputState();
    this.cleanupBoardFeedback({ terminal: true });
    this.resetTileTrayVisualState();
    const seconds = Math.max(1, Math.round((Date.now() - this.startMs) / 1000));
    const status = classifyResult(this.misses, this.hints, true);
    const result: Key9Result = { status, misses: this.misses, hints: this.hints, seconds, placed: this.lockedCells.size, key9SolvedDigits: getKey9SolvedDigits({ lockedCells: this.lockedCells }) };
    if (this.practice) {
      window.dispatchEvent(new CustomEvent('daysudoku:key9-practice-unlocked', { detail: result }));
      return;
    }
    window.dispatchEvent(new CustomEvent('daysudoku:key9-unlocked', { detail: result }));
  }

  private missOut() {
    this.completed = true;
    this.cancelKey9FeedbackUnlock();
    this.enterKey9TerminalInputState();
    this.cleanupBoardFeedback({ terminal: true });
    this.resetTileTrayVisualState();
    const seconds = Math.max(1, Math.round((Date.now() - this.startMs) / 1000));
    const result: Key9Result = { status: 'missed', misses: this.misses, hints: this.hints, seconds, placed: this.lockedCells.size, key9SolvedDigits: getKey9SolvedDigits({ lockedCells: this.lockedCells }) };
    if (this.practice) {
      window.dispatchEvent(new CustomEvent('daysudoku:key9-practice-missed', { detail: result }));
      return;
    }
    this.officialKey9Missed = true;
    window.dispatchEvent(new CustomEvent('daysudoku:key9-missed', { detail: result }));
  }

  private emitStatus() {
    const key9Progress = this.mode === 'key9' && !this.practice && !this.completed
      ? createKey9ProgressSnapshot({
          misses: this.misses,
          hints: this.hints,
          seconds: Math.max(0, Math.round((Date.now() - this.startMs) / 1000)),
          lockedCells: this.lockedCells
        })
      : undefined;

    window.dispatchEvent(new CustomEvent('daysudoku:key9-status', {
      detail: {
        phase: this.mode === 'full'
          ? this.completed ? 'full_done' : 'full_in_progress'
          : this.practice ? 'key9_practice' : 'key9_in_progress',
        misses: this.misses,
        hints: this.hints,
        locked: this.lockedCells.size,
        key9Progress,
        filled: this.mode === 'full' ? this.fullBoard.filter((value) => value !== '0').length : undefined,
        fullHints: this.mode === 'full' ? this.fullHints : undefined,
        notesMode: this.mode === 'full' ? this.notesMode : undefined,
        canHint: this.mode === 'full' ? this.canUseSelectedFullHint() : false,
        selectedCellIndex: this.selectedCellIndex,
        canUndo: this.mode === 'full' && !this.completed && this.fullUndoStack.length > 0,
        canErase: this.mode === 'full' && !this.completed && this.selectedCellIndex != null
          && this.isCellActionable(this.selectedCellIndex)
          && (this.fullBoard[this.selectedCellIndex] !== '0' || !!this.fullNotes[this.selectedCellIndex])
      }
    }));
  }

  private canUseSelectedFullHint(): boolean {
    return canUseFullHint(this.puzzle, this.fullBoard, this.fullLockedCells, this.selectedCellIndex);
  }
}
